import React, { useState } from 'react';
import { useApp } from '../appContext';

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { currentPage, navigate, isOnline } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = [
    { label: 'Audit', page: 'audit-menu' as const },
    { label: 'Dashboard', page: 'dashboard' as const },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="bg-[#4C9E00] text-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          {/* Logo & title */}
          <div className="flex items-center gap-3">
            {/* Nutrien leaf icon (SVG) */}
            <svg
              className="w-7 h-7 text-white"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 3C16 3 6 9 6 18C6 23.52 10.48 28 16 28C21.52 28 26 23.52 26 18C26 9 16 3 16 3Z"
                fill="rgba(255,255,255,0.25)"
                stroke="white"
                strokeWidth="1.5"
              />
              <path
                d="M16 28V14M16 14C16 14 11 17 9 22M16 14C16 14 21 17 23 22"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="font-bold text-base md:text-lg tracking-tight leading-tight">
              Cory Ground Control
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.page}
                onClick={() => navigate(item.page)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  currentPage === item.page ||
                  (item.page === 'audit-menu' && currentPage === 'inspection-form')
                    ? 'bg-white text-[#4C9E00] shadow'
                    : 'text-white hover:bg-white/20'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Online status + mobile menu */}
          <div className="flex items-center gap-3">
            {/* Online/offline indicator */}
            <div
              className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                isOnline
                  ? 'bg-white/20 text-white'
                  : 'bg-red-500/80 text-white'
              }`}
            >
              {/* Globe icon */}
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                <path
                  d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                {!isOnline && (
                  <line
                    x1="4"
                    y1="4"
                    x2="20"
                    y2="20"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  />
                )}
              </svg>
              <span className="hidden sm:inline">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            {/* Hamburger menu (mobile) */}
            <button
              className="md:hidden p-1.5 rounded hover:bg-white/20 transition"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/20 bg-[#3D8000] px-4 py-2">
            {navItems.map((item) => (
              <button
                key={item.page}
                onClick={() => {
                  navigate(item.page);
                  setMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium mb-1 transition ${
                  currentPage === item.page ||
                  (item.page === 'audit-menu' && currentPage === 'inspection-form')
                    ? 'bg-white text-[#4C9E00]'
                    : 'text-white hover:bg-white/20'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
